import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, DollarSignIcon, FileTextIcon, CreditCardIcon, FileSearchIcon, ClipboardCheckIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Property Tax Services
{
  id: 'pt1',
  title: 'Property Tax Bill Inquiry',
  description: 'Request property tax statement and billing information',
  category: 'Property Tax Services',
  estimatedTime: '1-2 business days',
  fee: '$25.00'
}, {
  id: 'pt2',
  title: 'Property Tax Payment Processing',
  description: 'Process property tax payments for current or outstanding bills',
  category: 'Property Tax Services',
  estimatedTime: 'Same day',
  fee: 'No fee'
}, {
  id: 'pt3',
  title: 'Property Tax Installment Plan',
  description: 'Set up monthly installment payments for property taxes',
  category: 'Property Tax Services',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
}, {
  id: 'pt4',
  title: 'Property Tax Clearance Certificate',
  description: 'Request official tax clearance certificate',
  category: 'Property Tax Services',
  estimatedTime: '3-5 business days',
  fee: '$50.00'
},
// Payment Processing
{
  id: 'pp1',
  title: 'Service Fee Payment Processing',
  description: 'Pay for municipal services including waste, sewer, and lighting',
  category: 'Payment Processing',
  estimatedTime: 'Same day',
  fee: 'No fee'
}, {
  id: 'pp2',
  title: 'Local Improvement Charge Payment',
  description: 'Process payments for local improvement charges',
  category: 'Payment Processing',
  estimatedTime: 'Same day',
  fee: 'No fee'
}, {
  id: 'pp3',
  title: 'BIA Levy Payment',
  description: 'Process Business Improvement Area levy payments',
  category: 'Payment Processing',
  estimatedTime: 'Same day',
  fee: 'No fee'
}, {
  id: 'pp4',
  title: 'Municipal Fines Payment',
  description: 'Pay bylaw infractions, sanitation fines, and other penalties',
  category: 'Payment Processing',
  estimatedTime: 'Same day',
  fee: 'No fee'
},
// Applications & Appeals
{
  id: 'aa1',
  title: 'Property Tax Exemption Application',
  description: 'Apply for property tax exemption or relief',
  category: 'Applications & Appeals',
  estimatedTime: '10-15 business days',
  fee: '$75.00'
}, {
  id: 'aa2',
  title: 'Service Fee Exemption Application',
  description: 'Request exemption from municipal service fees',
  category: 'Applications & Appeals',
  estimatedTime: '7-10 business days',
  fee: '$50.00'
}, {
  id: 'aa3',
  title: 'Property Valuation Appeal',
  description: 'Submit an appeal for property value assessment',
  category: 'Applications & Appeals',
  estimatedTime: '15-20 business days',
  fee: '$100.00'
}, {
  id: 'aa4',
  title: 'Penalty/Interest Waiver Request',
  description: 'Apply for waiver of penalties or interest charges',
  category: 'Applications & Appeals',
  estimatedTime: '5-7 business days',
  fee: '$25.00'
},
// Account Services
{
  id: 'as1',
  title: 'Revenue Account Statement',
  description: 'Request comprehensive municipal revenue account statement',
  category: 'Account Services',
  estimatedTime: '2-3 business days',
  fee: '$15.00'
}, {
  id: 'as2',
  title: 'Payment Arrangement for Arrears',
  description: 'Set up payment plan for outstanding balances',
  category: 'Account Services',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Property Tax Services':
      return <FileTextIcon className="h-6 w-6 text-blue-600" data-id="element-5371" />;
    case 'Payment Processing':
      return <CreditCardIcon className="h-6 w-6 text-green-600" data-id="element-5372" />;
    case 'Applications & Appeals':
      return <ClipboardCheckIcon className="h-6 w-6 text-orange-600" data-id="element-5373" />;
    case 'Account Services':
      return <FileSearchIcon className="h-6 w-6 text-purple-600" data-id="element-5374" />;
    default:
      return <DollarSignIcon className="h-6 w-6 text-gray-600" data-id="element-5375" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-5376">
      <div className="p-5 flex flex-col h-full" data-id="element-5377">
        <div className="flex-1" data-id="element-5378">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-5379">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-5380">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-5381">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-5382">
            <div className="flex items-center" data-id="element-5383">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-5384">
                ⏱
              </span>
              <span data-id="element-5385">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-5386">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-5387">
                💰
              </span>
              <span data-id="element-5388">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-5389">
            Process Payment
            <span className="ml-1" data-id="element-5390">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-5391">
      <div className="flex items-center mb-4" data-id="element-5392">
        <CategoryIcon category={category} data-id="element-5393" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-5394">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5395">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-5396" />)}
      </div>
    </div>;
};
export const TaxesRevenuePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-5397">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5398">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-5399">
          <a href="/" className="hover:text-gray-700" data-id="element-5400">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5401" />
          <span className="text-gray-900" data-id="element-5402">Taxes & Revenue</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-5403">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-5404">
            Taxes & Revenue Services
          </h1>
          <p className="text-gray-600" data-id="element-5405">
            Manage your municipal taxes, payments, and related services
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-5406">
          <div className="max-w-md relative" data-id="element-5407">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5408">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-5409" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-5410" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-5411" />)}
      </div>
    </div>;
};